import React, { useEffect, useState, useCallback } from 'react';
import VariableGraph from './VariableGraph';
import '../../styles/graphs.css';
import Container from 'react-bootstrap/Container';
import { useNavigate } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';



import format from 'date-fns/format';
import addDays from 'date-fns/addDays';
import subDays from 'date-fns/subDays';
import { getMeasurements, getDailyMeasurements } from '../../services/sensorGraphs/sensorGraphsService';
import LoadingComponent from '../loading/LoadingComponent';
import { GRAPHS_LOADING_MESSAGE } from '../utils/Constants';
import GraphDataNotFound from './GraphDataNotFound';
import {
    operationForChangeVbValue,
    operationForChangeCeValue,
    operationForChangePhValue,
    operationForAdaptDailyValue
} from '../utils/AdjustData';
import {
    SINGLE_VALUE_DAILY_VARS_CONTS,
    VARIABLES_WITH_ADJUSTED_CE,
    VARIABLES_WITH_ADJUSTED_PH
} from '../utils/Constants';
import { useUser } from '../../context/UserContext';

const VARIABLES_TO_HIDE_IN_DAILY = ['CB', 'VB', 'Rs'];
const SINGLE_VALUE_DAILY_VARS = SINGLE_VALUE_DAILY_VARS_CONTS;


const VariableGraphContainer = () => {

    const [sensor] = useState(() => {
        const sensorData = localStorage.getItem('actual-sensor');
        return sensorData ? JSON.parse(sensorData) : null;
    });
    const [loaded, setLoaded] = useState(false);
    const [showGraphs, setShowGraphs] = useState(false);
    const [variablesData, setVariablesData] = useState([]);
    const navigate = useNavigate();
    const { user, loading: userLoading } = useUser();

    // New State for View Mode
    const [viewMode, setViewMode] = useState('realtime'); // 'realtime' or 'daily'

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
    };

    const setThresholdForEachGraph = useCallback((dataResponse) => {
        return dataResponse.map((dataSensor) => {
            const data = dataSensor.data;

            // Las operaciones en función de la variableId (only for realtime usually, but keeping for safety)

            if (data.variableId === 'VB') operationForChangeVbValue(data.measures);
            if (VARIABLES_WITH_ADJUSTED_CE.includes(data.variableId)) operationForChangeCeValue(data.measures, sensor.id);
            if (VARIABLES_WITH_ADJUSTED_PH.includes(data.variableId)) operationForChangePhValue(data.measures, sensor.id);


            if (viewMode === 'daily' && SINGLE_VALUE_DAILY_VARS.includes(data.variableId)) {
                operationForAdaptDailyValue(data.measures);
            }

            return {
                data: [
                    {
                        id: data.variableId,
                        data: data.measures,
                    },
                ],
                varId: data.variableId,
                varName: data.variableName,
                units: data.variableUnit,
                initialMaxThreshold: data.threshold ? data.threshold.maxLimit : 0,
                actualMaxThreshold: data.threshold ? data.threshold.maxValue : 0,
                initialMinThreshold: data.threshold ? data.threshold.minLimit : 0,
                actualMinThreshold: data.threshold ? data.threshold.minValue : 0,
                sensorId: sensor.id,
                viewMode: viewMode // Pass viewMode to child
            };
        });
    }, [sensor.id, viewMode]);

    const requestAllMeasures = useCallback(async () => {
        if (!sensor || userLoading) return;

        setLoaded(false); // Show loading while fetching

        const variableAccessMap = {
            'CB': ['ROLE_ADMIN'],
            'VP': ['GET_VAR_PANEL'],
            'CP': ['GET_VAR_PANEL'],
            'AH': ['ROLE_ADMIN'],
            'PR': ['ROLE_ADMIN'],
            'EVT': ['GET_OWN_CROPS'],
            'RHS': ['GET_OWN_CROPS'],
        };

        let filteredVariables = sensor.sensorVariablesString.filter((idVariable) => {
            const requiredAuthorities = variableAccessMap[idVariable];
            if (SINGLE_VALUE_DAILY_VARS.includes(idVariable) && viewMode === 'realtime') {
                return false;
            }
            if (viewMode === 'daily' && VARIABLES_TO_HIDE_IN_DAILY.includes(idVariable)) {
                return false;
            }
            if (requiredAuthorities) {
                return user && user.authorities.some(auth => requiredAuthorities.includes(auth));
            }
            return true;
        });


        try {
            // Set default date ranges based on view mode
            const dateRange = viewMode === 'realtime'
                ? {
                    startDate: format(addDays(new Date(), -1), "yyyy-MM-dd"),
                    endDate: format(addDays(new Date(), 1), "yyyy-MM-dd")
                }
                : {
                    startDate: format(subDays(new Date(), 7), "yyyy-MM-dd"),
                    endDate: format(new Date(), "yyyy-MM-dd")
                };

            // Format dates for API
            const start = `${dateRange.startDate}T00:00:00.000-05:00`;
            const end = `${dateRange.endDate}T23:59:59.000-05:00`;

            const responses = await Promise.all(
                filteredVariables.map((idVariable) => {
                    if (viewMode === 'daily') {
                        return getDailyMeasurements(sensor.id, idVariable, start, end);
                    } else {
                        return getMeasurements(sensor.id, idVariable, start, end);
                    }
                })
            );

            if (responses[0] && responses[0].status === 401) {
                navigate('/');
                return;
            }

            // Check if any response has data
            const dataFound = responses.some(res => res.data.measures.length > 0);

            setLoaded(true);
            setShowGraphs(dataFound);
            setVariablesData(setThresholdForEachGraph(responses));

        } catch (error) {
            console.error(error);
            // navigate('/error'); // Optional: don't navigate on simple fetch error, maybe show toast
            setLoaded(true);
        }
    }, [sensor, navigate, setThresholdForEachGraph, user, viewMode, userLoading]);

    useEffect(() => {
        requestAllMeasures();
    }, [requestAllMeasures]);

    if (loaded) {
        return (
            <div>
                <Container fluid="xl" className="mb-4 mt-3">
                    <Row className="align-items-center justify-content-between">
                        <Col md={4}>
                            <h2 style={{ marginBottom: '0' }}>{sensor.name}</h2>
                        </Col>
                        <Col md={8} className="d-flex justify-content-end gap-3 align-items-center">
                            {/* View Mode Toggle */}
                            <div className="btn-group" role="group">
                                <button
                                    type="button"
                                    className={`btn ${viewMode === 'realtime' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => handleViewModeChange('realtime')}
                                >
                                    Tiempo Real
                                </button>
                                <button
                                    type="button"
                                    className={`btn ${viewMode === 'daily' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => handleViewModeChange('daily')}
                                >
                                    Diario
                                </button>
                            </div>
                        </Col>
                    </Row>
                </Container>

                {showGraphs ? (
                    <Container fluid="xl">
                        {/* Renderizar los gráficos */}
                        {variablesData.map((graphData, index) => (
                            <VariableGraph
                                key={index}
                                values={{ graphData }}
                                title="sensor"
                                viewMode={viewMode} // Pass viewMode
                            />
                        ))}
                    </Container>
                ) : (
                    <GraphDataNotFound />
                )}
            </div>
        );
    } else {
        return <LoadingComponent data={{ message: GRAPHS_LOADING_MESSAGE, type: 'success' }} />;
    }
};

export default VariableGraphContainer;